import { useRef, useCallback } from 'react'
import { float32ToInt16 } from '@/utils/audio'

type STTMessage =
  | { type: 'ready' }
  | { type: 'restarted' }
  | { type: 'partial'; text: string }
  | { type: 'final_transcript'; text: string; segment: string }
  | { type: 'reaction_ready'; segment: string; duration_ms: number }
  | { type: 'error'; message: string }

interface UseRealtimeSTTOptions {
  getSessionId: () => string
  getSessionToken?: () => string
  onReactionReady?: (segment: string, durationMs: number) => void
  onPartialTranscript?: (text: string) => void
  onFinalTranscript?: (segment: string, text: string) => void
  onError?: (msg: string) => void
}

const MAX_RECONNECT = 3

export function useRealtimeSTT({
  getSessionId,
  getSessionToken,
  onReactionReady,
  onPartialTranscript,
  onFinalTranscript,
  onError,
}: UseRealtimeSTTOptions) {
  const wsRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const reactionResolver = useRef<((val: { segment: string; durationMs: number }) => void) | null>(null)
  const reconnectAttempts = useRef(0)
  const intentionalClose = useRef(false)
  // Stable ref so the onclose reconnect handler always calls the latest startAudio
  const startAudioRef = useRef<() => void>(() => {})

  // Phase 1: Open WS connection, wait for OPEN — returns Promise<void>
  const connect = useCallback(
    (stream: MediaStream): Promise<void> => {
      streamRef.current = stream
      intentionalClose.current = false
      // Warm up AudioContext once so it's ready before startAudio is called
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext({ sampleRate: 16000 })
      }
      return new Promise((resolve, reject) => {
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        let wsUrl = `${protocol}://${window.location.host}/ws/realtime-stt/${getSessionId()}`

        // Attach auth tokens as query params
        const params: string[] = []
        const sessionToken = getSessionToken?.()
        if (sessionToken) params.push(`token=${encodeURIComponent(sessionToken)}`)
        // Also pass JWT as fallback auth
        try {
          const tokens = localStorage.getItem('tembo_tokens')
          if (tokens) {
            const { access } = JSON.parse(tokens)
            if (access) params.push(`access_token=${encodeURIComponent(access)}`)
          }
        } catch { /* ignore */ }
        if (params.length > 0) wsUrl += `?${params.join('&')}`

        const ws = new WebSocket(wsUrl)
        wsRef.current = ws

        const timeout = setTimeout(() => {
          // Close the half-open socket. Without this it can still finish the
          // handshake later and stream audio as a zombie connection after
          // the caller already decided to continue without STT.
          intentionalClose.current = true
          try { ws.close() } catch { /* noop */ }
          if (wsRef.current === ws) wsRef.current = null
          reject(new Error('STT WebSocket connection timeout'))
        }, 5000)

        ws.onopen = () => {
          clearTimeout(timeout)
          reconnectAttempts.current = 0
          resolve()
        }

        ws.onerror = () => {
          clearTimeout(timeout)
          reject(new Error('STT WebSocket error'))
        }

        ws.onmessage = (event) => {
          try {
            const msg: STTMessage = JSON.parse(event.data)
            if (msg.type === 'partial') {
              onPartialTranscript?.(msg.text)
            }
            if (msg.type === 'final_transcript') {
              onFinalTranscript?.(msg.segment, msg.text)
            }
            if (msg.type === 'reaction_ready') {
              reactionResolver.current?.({ segment: msg.segment, durationMs: msg.duration_ms })
              reactionResolver.current = null
              onReactionReady?.(msg.segment, msg.duration_ms)
            }
            if (msg.type === 'error') onError?.(msg.message)
          } catch {
            // binary or malformed – ignore
          }
        }

        ws.onclose = () => {
          // If reaction was pending, resolve with fallback so interview doesn't hang
          reactionResolver.current?.({ segment: '', durationMs: 6000 })
          reactionResolver.current = null

          // Auto-reconnect on unexpected close (network blip)
          if (!intentionalClose.current && reconnectAttempts.current < MAX_RECONNECT && streamRef.current) {
            const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 10000)
            reconnectAttempts.current++
            setTimeout(() => {
              if (!intentionalClose.current && streamRef.current) {
                connect(streamRef.current)
                  .then(() => startAudioRef.current())
                  .catch(() => { /* give up after MAX_RECONNECT */ })
              }
            }, delay)
          }
        }
      })
    },
    [getSessionId, onReactionReady, onError]
  )

  // Phase 2: Start streaming audio to the already-open WS (call after avatar finishes speaking)
  const startAudio = useCallback(() => {
    const ws = wsRef.current
    const stream = streamRef.current
    const ctx = audioContextRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !stream || !ctx) return

    // Don't create duplicate pipeline — guard on processor, not context
    if (processorRef.current) return

    const source = ctx.createMediaStreamSource(stream)
    sourceRef.current = source
    const processor = ctx.createScriptProcessor(4096, 1, 1)
    processorRef.current = processor

    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return
      // Backpressure: skip frame if send buffer is backing up (~400ms of 16kHz audio)
      if (ws.bufferedAmount > 65536) return
      const float32 = e.inputBuffer.getChannelData(0)
      const int16 = float32ToInt16(float32)
      ws.send(int16.buffer as any)
    }

    source.connect(processor)
    processor.connect(ctx.destination)
  }, [])

  // Keep ref in sync so reconnect handler always calls latest version
  startAudioRef.current = startAudio

  // Stop audio pipeline (but keep WS and AudioContext open — context stays warm for next segment)
  const stopAudio = useCallback(() => {
    processorRef.current?.disconnect()
    sourceRef.current?.disconnect()
    processorRef.current = null
    sourceRef.current = null
  }, [])

  // Send stop + react command; returns reaction duration with 30s timeout
  const sendStopAndReact = useCallback(
    (segment: string, questionContext: string, nextContext: string, nextTtsText?: string): Promise<{ segment: string; durationMs: number }> => {
      stopAudio()
      const reactionPromise = new Promise<{ segment: string; durationMs: number }>((resolve) => {
        reactionResolver.current = resolve
        wsRef.current?.send(
          JSON.stringify({
            type: 'stop_and_react',
            segment,
            question_context: questionContext,
            next_context: nextContext,
            next_tts_text: nextTtsText ?? '',
          })
        )
      })
      const timeoutPromise = new Promise<{ segment: string; durationMs: number }>((resolve) =>
        setTimeout(() => resolve({ segment, durationMs: 6000 }), 30_000)
      )
      return Promise.race([reactionPromise, timeoutPromise])
    },
    [stopAudio]
  )

  const disconnect = useCallback(() => {
    intentionalClose.current = true
    reconnectAttempts.current = 0
    stopAudio()
    audioContextRef.current?.close()
    audioContextRef.current = null
    wsRef.current?.close()
    wsRef.current = null
    streamRef.current = null
  }, [stopAudio])

  return { connect, startAudio, stopAudio, sendStopAndReact, disconnect, wsRef }
}
